import * as React from 'react'
import { SettingsSectionWithTabs } from '../../components/SettingsSectionWithTabs'
import { FieldsetSwitch } from '../../components/FieldsetSwitch'
import { FieldsetSelect } from '../../components/FieldsetSelect'
import { useQueryPayrollSettings } from '@/lib/api/company/configuration/payrollModule/useQueryPayrollSettings'
import { useMutationPayrollSettings } from '@/lib/api/company/configuration/payrollModule/useMutationPayrollSettings'

import { AbsenceComponentModel, PayrollSettingsQueryModel } from '@/lib/api/company/configuration/payrollModule/types'
import { PayrollTable } from './PayrollTable'

import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import { defaultPayrollSettings } from './config'
import { FieldsetInput } from '../../components/FieldsetInput'
import { SettingsPageWrapper } from '../../components/SettingsPageWrapper'
import { usePersistentTab } from '@/modules/shared/stores/tabsStore'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { RouteComponentProps } from '@/app/navigation/withRouter'

// Alert reminders configuration page
export const PayrollModulePageContent = (
  props: RouteComponentProps<{ companyId: string }>,
) => {
  const { t } = useLocalize()
  const companyId = props.match.params.companyId
  const readOnly = false
  const wrapConfirm = useWrapConfirm()
  const [selectedTab, setSelectedTab] = usePersistentTab(
    'settings-payroll',
    'a',
  )

  const q = useQueryPayrollSettings({ companyId })
  const mutation = useMutationPayrollSettings(companyId)
  const [changes, setChanged] = React.useState<
    Partial<PayrollSettingsQueryModel>
  >({})

  const state: PayrollSettingsQueryModel = structuredClone({
    ...(q.data ?? {}),
    ...changes,
    absenceComponents:
      changes?.absenceComponents ?? q?.data?.absenceComponents ?? [],
  } as PayrollSettingsQueryModel)

  const isDirty = Object.values(changes).length > 0

  const handleChangeBoolean = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name, checked } = e.target
      setChanged((changed) => ({
        ...changed,
        [name]: checked,
      }))
    },
    [],
  )

  const handleChangeNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target
    setChanged((state) => ({ ...state, [name]: value === '' ? null : Number(value) }))
  }

  const handleSelect = (value: string, props: { name: string }) => {
    const { name } = props 
    setChanged((state) => {
      const newState = structuredClone(state)
      if(name === 'leaveSalaryPaymentTiming' || name === 'sickSalaryPaymentTiming'){
        newState['leaveSalaryPaymentTiming'] = value as "endOfMonth" | "nextMonth"
        newState['sickSalaryPaymentTiming'] = value as "endOfMonth" | "nextMonth"
      } else {
        newState[name] = value
      }
      return newState
    })
  }

  const handleSave = () => {
    if (state) {
      mutation.mutate(state)
      handleReset()
    }
  }

  const handleReset = React.useCallback(() => {
    setChanged({})
  }, [q.data])

  const diffLabels = React.useMemo<Record<string, string>>(() => ({
    subtractPPK: t('configuration-payroll.deduct-ppk'),
    includeBonusAToPremium: t('configuration-payroll.include-bonus-a-to-premium'),
    decemberSalaryBasedOnJanuaryParams: t('configuration-payroll.december-salary-january'),
    includeLeavesAndSickInRecords: t('configuration-payroll.include-leaves-in-records'),
    calculateLeaveAndSickAmounts: t('configuration-payroll.calculate-leave-amounts'),
    sickLeaveMode: t('configuration-timesheet.sick-leave-base'),
    accidentContributionRate: t('configuration-payroll.accident-contribution-rate'),
    variableComponentsFromHours: t('configuration-payroll.variable-components-hours'),
    variableLeaveComponentsCalc: t('configuration-payroll.variable-leave-calc-period'),
    leaveSalaryPaymentTiming: t('configuration-payroll.salary-payment-timing'),
    sickSalaryPaymentTiming: t('configuration-payroll.salary-payment-timing'),
    absenceComponents: t('configuration.payrollModule.absenceComponents.title'),
  }), [t])

  const diffFormatters = React.useMemo<Record<string, (v: unknown) => string>>(() => ({
    subtractPPK: (v) => v === 'inCurrentMonth' ? t('configuration-payroll.in-current-month') : t('configuration-payroll.in-next-month'),
    variableComponentsFromHours: (v) => v === 'real' ? t('configuration-payroll.real-hours') : t('configuration-payroll.nominal-hours'),
    variableLeaveComponentsCalc: (v) => v === 'automatic' ? t('configuration-payroll.automatic') : v === '3m' ? t('configuration-payroll.3-months') : t('configuration-payroll.12-months'),
    leaveSalaryPaymentTiming: (v) => v === 'endOfMonth' ? t('configuration-payroll.end-of-month') : t('configuration-payroll.in-next-month'),
    sickSalaryPaymentTiming: (v) => v === 'endOfMonth' ? t('configuration-payroll.end-of-month') : t('configuration-payroll.in-next-month'),
    sickLeaveMode: (v) => v === 'grossZus' ? t('sickLeaveBaseType.GrossZus') : v === 'fromContract' ? t('sickLeaveBaseType.FromContract') : String(v),
  }), [t])

  const diffRenderers = React.useMemo<Record<string, (before: unknown, after: unknown) => { before: React.ReactNode; after: React.ReactNode }>>(() => ({
    absenceComponents: (before, after) => {
      const beforeArr = (before as AbsenceComponentModel[]) ?? []
      const afterArr = (after as AbsenceComponentModel[]) ?? []
      const fields = ['includeInLeave', 'isFixedComponent', 'includeInSick'] as const
      const colLabels: Record<typeof fields[number], string> = {
        includeInLeave: t('configuration.payrollModule.absenceComponents.includeInLeave'),
        isFixedComponent: t('configuration.payrollModule.absenceComponents.isFixedComponent'),
        includeInSick: t('configuration.payrollModule.absenceComponents.includeInSickPay'),
      }
      const changed = afterArr.filter(ac => {
        const bac = beforeArr.find(b => b.code === ac.code)
        return bac && fields.some(f => bac[f] !== ac[f])
      })
      if (!changed.length) return { before: <span>—</span>, after: <span>—</span> }
      return {
        before: (
          <div className="tw-flex tw-flex-col tw-gap-1">
            {changed.map(ac => {
              const bac = beforeArr.find(b => b.code === ac.code)
              const changedFields = fields.filter(f => bac?.[f] !== ac[f])
              return (
                <div key={ac.code} className="tw-text-xs">
                  <span className="tw-font-medium">{t(`configuration.payrollModule.absenceComponents.rows.${ac.code}`)}: </span>
                  {changedFields.map(f => (
                    <span key={f} className="tw-line-through tw-mr-1">{colLabels[f]}: {bac?.[f] ? 'Tak' : 'Nie'}</span>
                  ))}
                </div>
              )
            })}
          </div>
        ),
        after: (
          <div className="tw-flex tw-flex-col tw-gap-1">
            {changed.map(ac => {
              const changedFields = fields.filter(f => beforeArr.find(b => b.code === ac.code)?.[f] !== ac[f])
              return (
                <div key={ac.code} className="tw-text-xs">
                  <span className="tw-font-medium">{t(`configuration.payrollModule.absenceComponents.rows.${ac.code}`)}: </span>
                  {changedFields.map(f => (
                    <span key={f} className="tw-mr-1">{colLabels[f]}: {ac[f] ? 'Tak' : 'Nie'}</span>
                  ))}
                </div>
              )
            })}
          </div>
        ),
      }
    },
  }), [t])

  return (
    <SettingsPageWrapper
      isReady={!!state}
      isPending={q.isFetching || mutation.isPending}
      isError={q.isError}
      handleSave={handleSave}
      handleReset={handleReset}
      isDirty={isDirty}
      errorMsg={t('configuration-payroll.load-error')}
      globalErrorMsg={t('configuration-payroll.load-error-global')}
      diffBefore={(q.data ?? {}) as Record<string, unknown>}
      diffAfter={{ ...(q.data ?? {}), ...changes } as Record<string, unknown>}
      diffLabels={diffLabels}
      diffFormatters={diffFormatters}
      diffRenderers={diffRenderers}
    >
      <SettingsSectionWithTabs
        title=""
        selected={selectedTab}
        setSelected={setSelectedTab}
      >
        <div
          key={1}
          data-tab-name="a"
          title={t('configuration-payroll.tab-payroll')}
          className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
        >
          {/* Deduct PPK */}
          <FieldsetSelect
            label={t('configuration-payroll.deduct-ppk')}
            name="subtractPPK"
            options={[
              { key: 1, value: 'inCurrentMonth', label: t('configuration-payroll.in-current-month') },
              { key: 2, value: 'inNextMonth', label: t('configuration-payroll.in-next-month') },
            ]}
            value={state.subtractPPK || defaultPayrollSettings.subtractPPK}
            handleChange={handleSelect}
            disabled={readOnly}
          />

          {/* Add Award A to Bonus */}
          <FieldsetSwitch
            label={t('configuration-payroll.include-bonus-a-to-premium')}
            name="includeBonusAToPremium"
            checked={
              state.includeBonusAToPremium ||
              defaultPayrollSettings.includeBonusAToPremium
            }
            onChange={handleChangeBoolean}
            disabled={readOnly}
          />

          {/* December salary settled in January */}
          <FieldsetSwitch
            label={t('configuration-payroll.december-salary-january')}
            name="decemberSalaryBasedOnJanuaryParams"
            checked={
              state.decemberSalaryBasedOnJanuaryParams ||
              defaultPayrollSettings.decemberSalaryBasedOnJanuaryParams
            }
            onChange={handleChangeBoolean}
            disabled={readOnly}
          />

          {/* Place calculated vacation & sick days in records */}
          <FieldsetSwitch
            label={t('configuration-payroll.include-leaves-in-records')}
            name="includeLeavesAndSickInRecords"
            checked={
              state.includeLeavesAndSickInRecords ||
              defaultPayrollSettings.includeLeavesAndSickInRecords
            }
            onChange={handleChangeBoolean}
            disabled={readOnly}
          />

          {/* Calculate amounts for vacation & sick leave */}
          <FieldsetSwitch
            label={t('configuration-payroll.calculate-leave-amounts')}
            name="calculateLeaveAndSickAmounts"
            checked={
              state.calculateLeaveAndSickAmounts ||
              defaultPayrollSettings.calculateLeaveAndSickAmounts
            }
            onChange={handleChangeBoolean}
            disabled={readOnly}
          />

          {/* Wliczaj do podstawy chorobowego */}
          <FieldsetSelect
            label={t('configuration-timesheet.sick-leave-base')}
            name="sickLeaveMode"
            options={[
              { key: 1, value: 'grossZus', label: t('sickLeaveBaseType.GrossZus') },
              { key: 2, value: 'fromContract', label: t('sickLeaveBaseType.FromContract') },
            ]}
            value={state.sickLeaveMode || defaultPayrollSettings.sickLeaveMode}
            handleChange={handleSelect}
            disabled={readOnly}
          />

          {/* Accident insurance premium */}
          <FieldsetInput
            type="number"
            label={t('configuration-payroll.accident-contribution-rate')}
            name="accidentContributionRate"
            value={state.accidentContributionRate != null ? String(state.accidentContributionRate) : ''}
            placeholder="1.67"
            min={0}
            max={10}
            step={0.01}
            disabled={readOnly}
            onChange={handleChangeNumber}
          />
        </div>
        <div
          key={1}
          data-tab-name="b"
          title={t('configuration-payroll.tab-absence-params')}
          className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
        >
          {/* Liczenie składników zmiennych z godzin */}
          <FieldsetSelect
            label={t('configuration-payroll.variable-components-hours')}
            name="variableComponentsFromHours"
            options={[
              { key: 1, value: 'real', label: t('configuration-payroll.real-hours') },
              { key: 2, value: 'nominal', label: t('configuration-payroll.nominal-hours') },
            ]}
            value={
              state.variableComponentsFromHours ||
              defaultPayrollSettings.variableComponentsFromHours
            }
            handleChange={handleSelect}
            disabled={readOnly}
          />

          {/* Period for calculating variable absence components */}
          <FieldsetSelect
            label={t('configuration-payroll.variable-leave-calc-period')}
            name="variableLeaveComponentsCalc"
            options={[
              { key: 1, value: 'automatic', label: t('configuration-payroll.automatic') },
              { key: 2, value: '3m', label: t('configuration-payroll.3-months') },
              { key: 3, value: '12m', label: t('configuration-payroll.12-months') },
            ]}
            value={
              state.variableLeaveComponentsCalc ||
              defaultPayrollSettings.variableLeaveComponentsCalc
            }
            handleChange={handleSelect}
            disabled={readOnly}
          />

          {/* Salary payment period (vacations and sick leaves) */}
          <FieldsetSelect
            label={t('configuration-payroll.salary-payment-timing')}
            name="leaveSalaryPaymentTiming" // + sickSalaryPaymentTiming
            options={[
              {
                key: 1,
                value: 'endOfMonth',
                label: t('configuration-payroll.end-of-month'),
              },
              {
                key: 2,
                value: 'nextMonth',
                label: t('configuration-payroll.in-next-month'),
              },
            ]}
            value={
              state.leaveSalaryPaymentTiming ||
              defaultPayrollSettings.leaveSalaryPaymentTiming
            }
            handleChange={handleSelect}
            disabled={readOnly}
          />
        </div>
        <div
          key={2}
          data-tab-name="c"
          title={t('configuration-payroll.tab-absence-calculation')}
          className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
        >
          <PayrollTable state={state} setState={setChanged} />
        </div>
      </SettingsSectionWithTabs>
    </SettingsPageWrapper>
  )
}
